import { isManualAddress } from '@service-victoria/component-library/components/AddressLookup/helpers';
import { Step as StepProps } from '@service-victoria/component-library/components/Wizard/props';
import type { Interfaces as AddressInterfaces } from '@service-victoria/component-library/components/AddressLookup/props';
import type { Props as AutoCompleteProps } from '@service-victoria/component-library/components/AutoComplete';
import { FormikErrors, FormikTouched } from 'formik';
import { lazy } from 'react';
import * as Yup from 'yup';

import * as Interfaces from 'app/interfaces';
import * as Steps from 'app/transactions/provider/steps';
import { constants } from 'app/utils';
import { isValidPhoneNumber } from 'app/utils/validations';
import { isEmpty } from 'lodash-es';

type Values = Pick<Interfaces.Common.ProviderFormValues, 'providerDetails'>;

const invalidPhoneNumber =
  'Your phone number must be exactly 10 digits and contain a valid area code (02, 03, 04, 07 or 08)';

const initialValues: Values = {
  providerDetails: {
    providerName: '',
    activityType: '',
    providerPhone: '',
    address: {
      fullDetails: {
        fullAddress: '',
        suburb: '',
        state: '',
        postCode: ''
      },
      summaryData: {
        label: '',
        value: ''
      }
    },
    role: ''
  }
};

const initialErrors: FormikErrors<Values> = {};
const initialTouched: FormikTouched<Values> = {};

const providerDetailsSchema = Yup.object<Interfaces.Common.ProviderDetails>().shape({
  providerName: Yup.string().required(constants.GenericValidationErrors.required),
  activityType: Yup.string().required(constants.GenericValidationErrors.required),
  providerPhone: Yup.string()
    .required(constants.GenericValidationErrors.required)
    .test('phoneNumberIsValid', invalidPhoneNumber, function validate(phoneNumber: string) {
      return isValidPhoneNumber(phoneNumber);
    }),
  address: Yup.object<AddressInterfaces.AddressData>().shape({
    fullDetails: Yup.object<AddressInterfaces.FullDetails>().shape({
      fullAddress: Yup.string()
        .required(constants.AddressValidationErrors.required)
        .test(
          'Not a PO Box',
          constants.AddressValidationErrors.noPOBox,
          function validate(address = '') {
            return !address.toLowerCase().includes('po box');
          }
        ),
      postCode: Yup.string()
        .required(constants.AddressValidationErrors.postcode)
        .min(4, constants.AddressValidationErrors.postcode),
      state: Yup.string().required(constants.AddressValidationErrors.state),
      suburb: Yup.string().required(constants.AddressValidationErrors.suburb)
    }),
    summaryData: Yup.mixed<AutoCompleteProps.OptionType>()
      .default(undefined)
      .test(
        'empty value',
        constants.AddressValidationErrors.required,
        function validate(address?: AutoCompleteProps.OptionType) {
          const value = this.parent as AddressInterfaces.AddressData;
          if (isManualAddress(value)) {
            return true;
          }
          return address ? !isEmpty(address.label) : false;
        }
      )
      .test(
        'no value selected from drop down',
        constants.AddressValidationErrors.noAddressChosen,
        function validate(address?: AutoCompleteProps.OptionType) {
          const value = this.parent as AddressInterfaces.AddressData;
          if (isManualAddress(value)) {
            return true;
          }
          return address ? !isEmpty(address.label) && !isEmpty(address.value) : false;
        }
      )
  }),
  role: Yup.string().required(constants.GenericValidationErrors.required)
});

const validationSchema = Yup.object<Values>().shape({
  providerDetails: providerDetailsSchema
});

const Component = lazy(() => import('./ProviderDetails'));

const Step: StepProps<Values> = {
  id: 'providerDetails',
  title: constants.ProviderDetailsPage.title,
  stepName: 'Update details',
  Component,
  validation: {
    schema: validationSchema,
    fieldNames: [
      'providerDetails.providerName',
      'providerDetails.activityType',
      'providerDetails.providerPhone',
      'providerDetails.address.summaryData',
      'providerDetails.address.fullDetails.fullAddress',
      'providerDetails.address.fullDetails.postCode',
      'providerDetails.address.fullDetails.state',
      'providerDetails.address.fullDetails.suburb',
      'providerDetails.role'
    ]
  },
  navigation: {
    normal: [
      {
        content: 'Back',
        type: 'secondary',
        shouldValidate: false,
        onClick: () => {
          window.location.href = `${import.meta.env.BASE_URL}${constants.Routes.Dashboard}` || '/';
        }
      },
      {
        content: 'Next',
        shouldValidate: true,
        type: 'primary',
        onClick: async (context, formProps): Promise<void> => {
          const formValues = formProps.values as Interfaces.Common.ProviderFormValues;

          context.dispatch({
            type: 'GOTO_STEP',
            stepId: Steps.Review.Step.id,
            values: formValues
          });
        }
      }
    ]
  }
};

export {
  Step,
  initialErrors,
  initialTouched,
  initialValues,
  providerDetailsSchema,
  validationSchema
};
